/**
 * 音声認識と会話制御 UI（プレゼンテーション）
 */
import React from "react";
import { Tooltip } from "react-tooltip";
import { PlaySound } from "~/SoundUtils";

// スタンバイモードの状態値
const standbyModes = {
  stopped: 0, // 停止状態
  standby: 1, // 待機状態
  active: 2, // アクティブ状態
};

// 疑似プレースホルダ表示制御
const getPseudoPlaceholderDisplay = (
  standbyMode: number,
  listeningMode: number,
  isPressed: boolean,
  isThinkingPlaceholder: boolean // t("thinking")を表示しているかどうか
): "none" | "block" =>
  !isPressed && listeningMode === 2 && standbyMode === standbyModes.active && !isThinkingPlaceholder
    ? "block"
    : "none";

interface ControlButtonLayout1Props {
  // 状態・値
  t: (key: string) => string;
  displayText: string;
  placeholderText: string;
  listeningMode: number;
  standbyMode: number;
  isPressed: boolean;
  isListening: boolean;
  transcript: string;
  volume: number;
  // ハンドラ
  setDisplayText: (value: string) => void;
  handleDeleteMessage: () => void;
  handleSendMessage: () => void;
  handlePressStart: () => void;
  handlePressEnd: () => void;
  handlePressLeave: () => void;
  handleTouchMove: (e: React.TouchEvent<HTMLButtonElement>) => void;
}

const ControlButtonLayout1: React.FC<ControlButtonLayout1Props> = ({
  t,
  displayText,
  placeholderText,
  listeningMode,
  standbyMode,
  isPressed,
  isListening,
  transcript,
  volume,
  setDisplayText,
  handleDeleteMessage,
  handleSendMessage,
  handlePressStart,
  handlePressEnd,
  handlePressLeave,
  handleTouchMove,
}) => {
  return (
    <div className="flex-textbox">
      <div className="chat-main-space">
        <textarea
          value={displayText}
          placeholder={placeholderText}
          readOnly={true}
          wrap={"soft"}
          className={`input-textbox ${
            listeningMode === 1
              ? " textbox-send-button-width"
              : listeningMode === 2
                ? " textbox-listen-button-width"
                : ""
          }`}
          onChange={(event) => {
            setDisplayText(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
            }
          }}
        />
      </div>

      {/* ボタン押下時聞き取りモード 疑似プレースホルダ */}
      <div
        className="pseudo-placeholder-text"
        style={{
          display: getPseudoPlaceholderDisplay(
            standbyMode,
            listeningMode,
            isPressed,
            placeholderText === t("thinking")
          ),
        }}
      >
        {t("listeningMode2PlaceholderIconBefore")}
        <button className={"press-mic-btn"} disabled aria-label="microphone icon">
          <span className="material-symbols-rounded press-mic-icon">mic</span>
        </button>
        {t("listeningMode2PlaceholderIconAfter")}
      </div>

      {/* listeningMode が 1 のとき */}
      <div
        className="chat-button-container send-mode-container"
        style={{ display: listeningMode === 1 ? "flex" : "none" }}
      >
        <button
          data-tooltip-id="tip_erase"
          className="btn"
          onClick={handleDeleteMessage}
          disabled={displayText.trim() === "" || transcript !== "" || !isListening}
          onMouseUp={() => PlaySound("push3", volume)}
        >
          <span className="material-symbols-outlined">cancel</span>
          <Tooltip id="tip_erase" place="top" content={t("delete")} style={{ zIndex: 0 }} />
        </button>
        <button
          data-tooltip-id="tip_send"
          className="btn"
          onClick={handleSendMessage}
          disabled={displayText.trim() === "" || transcript !== "" || !isListening}
          onMouseUp={() => PlaySound("push3", volume)}
        >
          <span className="material-symbols-rounded">send</span>
          <Tooltip id="tip_send" place="bottom" content={t("send")} style={{ zIndex: 0 }} />
        </button>
      </div>

      {/* listeningMode が 2 のとき */}
      <div
        className="chat-button-container button-mode-container"
        style={{ display: listeningMode === 2 ? "flex" : "none" }}
      >
        <button
          className={"press-mic-btn" + (isPressed ? " pressed" : "")}
          onMouseDown={handlePressStart}
          onMouseUp={handlePressEnd}
          onMouseLeave={handlePressLeave}
          onTouchStart={handlePressStart}
          onTouchEnd={handlePressEnd}
          onTouchMove={handleTouchMove}
          onTouchCancel={handlePressLeave}
          disabled={!isListening}
        >
          <span className="material-symbols-rounded press-mic-icon">mic</span>
        </button>
      </div>
    </div>
  );
};

export default ControlButtonLayout1;
